import { useValidateRowTimesheet } from '@oc/pages/importers_rsi/validations/impl/timesheet/useValidateRowTimesheet';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { useTaeFlags } from '@oc/pages/settings/TimesheetSettings/useTaeFlags';
import { filterNotEmpty } from '@utmost.co/utmost-design-system/src/utils/listHelpers';
import { FieldMetadataElementValidator } from 'src/components/fieldMetadata/FieldMetadataElementValidator';
import { AllowedImportType, CommonFieldDefinitionData } from '../utils/useImporterData';
import { RsiData, RsiFieldKey, RsiRowHook, RsiRowOnErrorCallback, RsiRowValidator } from '../Types';
import { useImportersIntl } from '../translations/useImportersIntl';
import { ValidationParams } from './impl/validationUtils';
import { useValidateRowAgainstRetroactiveEngagementChange } from './impl/engagementRetroactiveChange/useValidateRowAgainstRetroactiveEngagementChange';
import { useValidateRowAgainstEngagementOpenChangeSet } from './impl/engagementOpenChangeSet/useValidateRowAgainstEngagementOpenChangeSet';
import { ValidateImporterRowQuery } from './data/__generated__/ValidateImporterRow.graphql';
import { useValidateRowUsingMetadata } from './impl/metadata/useValidateRowUsingMetadata';
import { useValidateRowAgainstReferenceIdAndFieldExistence } from './impl/refIdAndExistence/useValidateRowAgainstReferenceIdAndFieldExistence';
import { ImportEntityTypeModel } from 'src/graphql/types';

export type ReferenceIdValidationsModel = DeeplyDefined<
  ValidateImporterRowQuery['validateReferenceIdsRow']['validations']
>;

export const useRowValidation = (
  importType: AllowedImportType | undefined,
  validationParams: ValidationParams | undefined,
  fieldsForExistenceDetection: RsiFieldKey[],
  fieldsForReferenceIdValidation: RsiFieldKey[],
  urnToFieldMetadataMap: Map<string, CommonFieldDefinitionData>,
  fieldMetadataValidator: FieldMetadataElementValidator,
  openChangeSetIdsRef: MutableRefObject<Set<string>>
): RsiRowHook => {
  const intl = useImportersIntl();
  const { costCenters, timesheets } = useTaeFlags();
  const entityName = importType?.entityType ? intl[importType.entityType] : 'record';
  // We want to show the BE message for Spend Approval Limit imports as we don't create a new record.
  const rowIsCreateMessage =
    importType?.entityType === ImportEntityTypeModel.SpendApprovalLimit ? '' : intl.rowIsCreate({ entityName });
  const rowIsUpdateMessage = intl.rowIsUpdate({ entityName });

  const refIdAndExistenceValidator = useValidateRowAgainstReferenceIdAndFieldExistence(
    {
      fieldsForExistenceDetection,
      fieldsForReferenceIdValidation,
      rowIsCreateMessage,
      rowIsUpdateMessage
    },
    validationParams
  );
  const engagementRetroActiveChangeValidator = useValidateRowAgainstRetroactiveEngagementChange();
  const timeSheetRowValidator = useValidateRowTimesheet();
  const metadataValidator = useValidateRowUsingMetadata({
    urnToFieldMetadataMap,
    fieldMetadataValidator
  });
  const openChangeSetRowValidator = useValidateRowAgainstEngagementOpenChangeSet(openChangeSetIdsRef);

  const validators: RsiRowValidator[] = useMemo(
    () =>
      filterNotEmpty([
        metadataValidator,
        importType?.id === 'TIMESHEET' && timesheets.validationForTimesheetCorrectionImport && timeSheetRowValidator,
        importType?.id === 'WORKER-RETROACTIVE-CHANGE' &&
          costCenters.retroChangeImportValidation &&
          engagementRetroActiveChangeValidator,
        refIdAndExistenceValidator,
        openChangeSetRowValidator
      ]),
    [
      costCenters.retroChangeImportValidation,
      timesheets.validationForTimesheetCorrectionImport,
      importType?.id,
      engagementRetroActiveChangeValidator,
      metadataValidator,
      refIdAndExistenceValidator,
      timeSheetRowValidator,
      openChangeSetRowValidator
    ]
  );

  return useCallback(
    async (row: RsiData, addError: RsiRowOnErrorCallback, table: RsiData[]) => {
      await Promise.all(validators.map((validator) => validator(row, addError, table)));
      return row;
    },
    [validators]
  );
};
